import "./styles.scss";
import { IWofOptions, WheelOfFortune } from "@phantom/ehub-components";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import Spin247Logo from "./images/logo_spin247.png";
import RuletaLogo from "./images/logo_wof_desktop.png";
import Spin247Error from "./Spin247Error";
import Spin247WinModal from "./Spin247WinModal";
import NotAvailableModal from "./NotAvailableModal";
import { checkIsMobile } from "../../utils/helpers";
import { LoadingAnimation } from "./Loading/LoadingAnimation";
import { IWofCTAParams } from "@phantom/ehub-components/interfaces/Wof";
import { sendMessage } from "../../contexts/messages/context";

interface IProps {
  options: IWofOptions;
}

export const WheelSpin247 = ({ options }: IProps): ReactElement => {
  const [renderCondition, setRenderCondition] = useState(false);
  const [resizeTimeStamp, setResizeTimeStamp] = useState<Date | null>(null);
  const [showWheel, setShowWheel] = useState<boolean>(true);

  const handleResize = (): void => {
    setResizeTimeStamp(new Date());
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (resizeTimeStamp) {
      setShowWheel(false);

      setTimeout(() => {
        const now = new Date();
        if (now.getTime() - resizeTimeStamp.getTime() > 300) {
          setShowWheel(true);
        }
      }, 1000);
    }
  }, [resizeTimeStamp]);

  const { width, insideRadius, style, highlightStyle } = useMemo(() => {
    const isLandscape = window?.innerWidth > window?.innerHeight;
    const isMobile = checkIsMobile();

    let height = window.innerHeight;
    let width = window.innerWidth;

    if (isMobile) {
      if (isLandscape) {
        height = window.innerWidth;
        width = window.innerHeight;
      }
    }

    return {
      width: width > 800 ? height * 0.4 : width * 0.8,
      insideRadius: width > 800 ? height * 0.02 : 20,
      style: [
        {
          color: "#f1cf00",
          fontWeight: "bold",
          fontSize: 20,
          fontFamily: "Helvetica, Arial",
          marginTop: 40,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
        },
        {
          color: "#ffffff",
          fontWeight: "bold",
          fontSize: 20,
          fontFamily: "Helvetica, Arial",
          marginTop: -80,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
        },
        {
          color: "#ffffff",
          fontWeight: "bold",
          fontSize: 20,
          fontFamily: "Helvetica, Arial",
          marginTop: -175,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
        },
        {
          color: "#ffffff",
          fontWeight: "bold",
          fontSize: 20,
          fontFamily: "Helvetica, Arial",
          marginTop: -175,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
        },
      ],
      highlightStyle: [
        {
          color: "#000",
          fontWeight: "bold",
          fontSize: 20,
          fontFamily: "Helvetica, Arial",
          marginTop: 40,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
        },
        {
          color: "#000",
          fontWeight: "bold",
          fontSize: 20,
          fontFamily: "Helvetica, Arial",
          marginTop: -80,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
        },
        {
          color: "#000",
          fontWeight: "bold",
          fontSize: 20,
          fontFamily: "Helvetica, Arial",
          marginTop: -175,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
        },
        {
          color: "#000",
          fontWeight: "bold",
          fontSize: 20,
          fontFamily: "Helvetica, Arial",
          marginTop: -175,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
        },
      ],
    };
  }, [window.innerWidth, showWheel]);

  useEffect(() => {
    if (showWheel) {
      let styleFontSize =
        window.innerWidth > 800 ? width * 0.0465 : width * 0.05;
      let marginTopTextOne =
        window.innerWidth > 800 ? width * 0.3 : width * 0.33;
      let marginTopTextTwo =
        window.innerWidth > 800 ? width * 0.4 : width * 0.43;
      let marginTopTextThree =
        window.innerWidth > 800 ? width * 0.5 : width * 0.53;
      let marginTopTextFour =
        window.innerWidth > 800 ? width * 0.6 : width * 0.6;

      style.map((item, index) => {
        item.fontSize = styleFontSize;

        if (index === 0) {
          item.marginTop = marginTopTextOne;
        }

        if (index === 1) {
          item.marginTop = marginTopTextTwo;
        }

        if (index === 2) {
          item.marginTop = marginTopTextThree;
        }

        if (index === 3) {
          item.marginTop = marginTopTextFour;
          item.fontSize =
            window.innerWidth > 800 ? width * 0.04 : width * 0.043;
        }
      });

      highlightStyle.map((item, index) => {
        item.fontSize = styleFontSize;

        if (index === 0) {
          item.marginTop = marginTopTextOne;
        }

        if (index === 1) {
          item.marginTop = marginTopTextTwo;
        }

        if (index === 2) {
          item.marginTop = marginTopTextThree;
        }

        if (index === 3) {
          item.marginTop = marginTopTextFour;
          item.fontSize =
            window.innerWidth > 800 ? width * 0.04 : width * 0.043;
        }
      });
    }
  }, [highlightStyle, style, width, showWheel]);

  useEffect(() => {
    setRenderCondition(
      !!options.playerId && !!options.gameId && !!options.campaignId
    );
  }, [options.gameId, options.playerId, options.campaignId]);

  const miniContainer = (options: IWofCTAParams) => (
    <>
      <div className="wof-container--body-ctas">
        <button onClick={() => sendMessage({ type: "REDIRECT_REGISTRATION" })}>
          No Account
        </button>
        <button onClick={() => onStart(options.onStart)}>START</button>
      </div>
    </>
  );

  let changeOverlayVisibility = false;

  const onStart = (callback: any) => {
    changeOverlayVisibility = true;
    callback();
  };

  return (
    <div className={"wrapper"}>
      <div className={"overlay"}></div>
      <div className={"content"}>
        <div className={"imgLogoContainer"}>
          <img src={Spin247Logo} className={"spin247-logo"} />
          <img src={RuletaLogo} className={"roulette-logo"} />
        </div>
        {renderCondition ? (
          <>
            {!showWheel ? (
              <LoadingAnimation />
            ) : (
              <WheelOfFortune
                onError={() => <Spin247Error />}
                renderHeader={undefined}
                options={options}
                renderOnlyWheel={true}
                wheelInModal={true}
                winModalContent={(ref, prize, onClose) => (
                  <Spin247WinModal prize={prize} />
                )}
                sliceSettings={{
                  sliceColor: "#101020",
                  highlightStyle: highlightStyle,
                  style: style,
                  formatFn: (segment) => {
                    return segment.title.split(" ");
                  },
                }}
                winModalTimeoutInMs={1000}
                wheelOptions={{
                  rootClass: "rootWheel",
                  // skinPath: "/spin247/wheel/ruleta_de_la_fortuna.png",
                  width: width,
                  insideRadius: insideRadius,
                  // borderColor: "yellow",
                  borderWidth: 3,
                  drawAngle: 315,
                  imageFrameClass: "wheel",
                }}
                prizesSection={{ renderCTASection: miniContainer }}
              />
            )}
          </>
        ) : (
          <NotAvailableModal
            playerId={options.playerId}
            campaignID={options.campaignId}
          />
        )}
      </div>
    </div>
  );
};
